import React from 'react';
import { motion, AnimatePresence, type Transition } from 'motion/react';
import { useLocation, useNavigate } from 'react-router';
import { clickBelongsToBrowser } from '../utils/navigationHelpers';

interface PageTransitionProps {
  children: React.ReactNode;
}

export function PageTransition({ children }: PageTransitionProps) {
  const location = useLocation();

  const pageVariants = {
    initial: {
      opacity: 0,
      y: 30,
      scale: 0.96,
      filter: "blur(3px)",
    },
    in: {
      opacity: 1,
      y: 0,
      scale: 1,
      filter: "blur(0px)",
    },
    out: {
      opacity: 0,
      y: -20,
      scale: 1.04,
      filter: "blur(2px)",
    }
  };

  // Typed as Transition so `type` and `ease` keep their literal shapes; inferred
  // as plain string/number[] motion rejects them.
  const pageTransition: Transition = {
    type: "tween",
    ease: [0.25, 0.46, 0.45, 0.94], // Enhanced easing for smoother feel
    duration: 0.25, // Sped up by 2x
  };


  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial="initial"
        animate="in"
        exit="out"
        variants={pageVariants}
        transition={pageTransition}
        onAnimationComplete={(definition) => {
          if (definition === 'in') window.dispatchEvent(new Event('waq:page-ready'));
        }}
        className="relative w-full min-h-screen"
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

// Loading transition component for instant feedback on navigation clicks
export function NavigationLoadingOverlay({ isLoading }: { isLoading: boolean }) {
  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
          className="fixed inset-0 z-[55] bg-white/80 backdrop-blur-sm pointer-events-none"
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.15, delay: 0.05 }}
              className="flex flex-col items-center space-y-4"
            >
              {/* Spinning loader with QA Launch branding */}
              <motion.div
                className="w-12 h-12 rounded-full border-4 border-qa-blue-100"
                style={{
                  borderTopColor: 'var(--qa-blue-600)',
                  borderRightColor: 'var(--qa-yellow-400)',
                }}
                animate={{ rotate: 360 }}
                transition={{
                  duration: 0.5,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              
              {/* Loading text */}
              <motion.p
                className="text-sm font-medium text-qa-blue-700"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15, delay: 0.1 }}
              >
                Loading...
              </motion.p>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Enhanced button transition component for immediate visual feedback
export function ButtonTransition({ 
  children, 
  onClick, 
  to,
  className = "",
  disabled = false,
  ...props 
}: {
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /**
   * Destination, for a CTA that navigates. Given one, this renders an anchor
   * with a real href instead of a `<button>`, so the link is crawlable and
   * cmd-click opens a new tab. The header CTAs were buttons, which meant Google
   * saw no path from the home page to the application forms at all.
   *
   * Not a react-router `Link`, which cannot carry the motion props this needs
   * for the ripple and press animation -- so the click logic `Link` would have
   * provided is applied by hand, via `clickBelongsToBrowser`.
   */
  to?: string;
  className?: string;
  disabled?: boolean;
  [key: string]: any;
}) {
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    if (!onClick && !to) return;

    // Add immediate visual feedback with ripple effect
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;
    
    // Create ripple element
    const ripple = document.createElement('div');
    ripple.style.cssText = `
      position: absolute;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.3);
      transform: scale(0);
      animation: ripple 0.3s linear;
      left: ${x}px;
      top: ${y}px;
      width: ${size}px;
      height: ${size}px;
      pointer-events: none;
      z-index: 1000;
    `;
    
    // Add ripple animation keyframes if not already added
    if (!document.querySelector('#ripple-styles')) {
      const style = document.createElement('style');
      style.id = 'ripple-styles';
      style.textContent = `
        @keyframes ripple {
          to {
            transform: scale(2);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    // Add ripple to button
    button.style.position = 'relative';
    button.style.overflow = 'hidden';
    button.appendChild(ripple);
    
    // Remove ripple after animation
    setTimeout(() => {
      if (button.contains(ripple)) {
        button.removeChild(ripple);
      }
    }, 300);
    
    onClick?.(event);

    if (!to) return;
    // A modified or middle click is the browser's: let it open a new tab.
    if (clickBelongsToBrowser(event)) return;
    event.preventDefault();
    navigate(to);
  };

  const Element = to && !disabled ? motion.a : motion.button;

  return (
    <Element
      className={`relative overflow-hidden ${className}`}
      onClick={handleClick}
      {...(to && !disabled ? { href: to } : { disabled })}
      whileHover={{ 
        scale: disabled ? 1 : 1.03,
        y: disabled ? 0 : -2,
        boxShadow: disabled ? undefined : "0 10px 25px rgba(0, 0, 0, 0.15)"
      }}
      whileTap={{ 
        scale: disabled ? 1 : 0.97,
        y: disabled ? 0 : 0,
      }}
      transition={{
        type: "spring",
        stiffness: 600,
        damping: 30
      }}
      {...props}
    >
      {/* Gradient overlay on hover */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 opacity-0"
        whileHover={{ opacity: disabled ? 0 : 1 }}
        transition={{ duration: 0.1 }}
      />
      
      {/* Button content */}
      <span className="relative z-10">
        {children}
      </span>
    </Element>
  );
}
